/**
 * PM Platform — controlador de vistas hermanas del drawer móvil.
 *
 * Arquitectura: dentro de .mobile-menu (shell nativo de Athens, sin
 * cambios), .pm-mobile-drawer contiene varias <section class="pm-mobile-view">
 * hermanas directas de .pm-mobile-drawer-views. Solo una está visible a la
 * vez mediante el atributo nativo `hidden` (retira del layout por completo,
 * no solo visualmente) + `aria-hidden` + `inert` (retira del árbol de
 * accesibilidad y del orden de tabulación). Este script solo mueve el
 * atributo `hidden` entre vistas -- Athens (assets/scripts.js) sigue siendo
 * el único responsable de abrir/cerrar `.mobile-menu`, su focus trap y el
 * bloqueo de scroll del body; este archivo nunca los reimplementa.
 */
(function () {
  "use strict";

  var initializedDrawers = new WeakSet();

  function initDrawer(drawerEl) {
    if (initializedDrawers.has(drawerEl)) return;
    initializedDrawers.add(drawerEl);

    var wrap = drawerEl.closest(".head-slot-nav-mobile-link-wrapper");
    var mobileMenu = wrap ? wrap.querySelector(".mobile-menu") : null;
    var toggle = wrap ? wrap.querySelector(".head-slot-nav-mobile-link") : null;
    var escapeTarget = mobileMenu || document;

    // [data-pm-root-view] es el selector estable y explícito de la raíz --
    // no se asume "la primera vista" ni se depende únicamente del nombre
    // de clase. Se conserva .pm-mobile-view-root como respaldo si el
    // atributo faltara en un markup desactualizado.
    var rootView = drawerEl.querySelector("[data-pm-root-view]") || drawerEl.querySelector(".pm-mobile-view-root");
    if (!rootView || !rootView.id) return;

    var stack = [rootView.id];

    function getView(id) {
      return id ? document.getElementById(id) : null;
    }

    function resetScroll(viewEl) {
      var scrollEl = viewEl.querySelector(".pm-mobile-view-scroll");
      if (scrollEl) scrollEl.scrollTop = 0;
    }

    function focusView(viewEl) {
      var back = viewEl.querySelector("[data-pm-back]");
      if (back) {
        back.focus();
        return;
      }
      var title = viewEl.querySelector(".pm-mobile-view-title");
      if (title) {
        if (!title.hasAttribute("tabindex")) title.setAttribute("tabindex", "-1");
        title.focus();
      }
    }

    // La visibilidad real de una vista SIEMPRE la determina el atributo
    // `hidden` (ver assets/pm-mobile-drawer.css: opacity:1/translateX(0)
    // son el estado por defecto de cualquier vista sin "hidden" -- nunca
    // dependen de una clase que agregue este script). "entering" es
    // exclusivamente la animación de entrada: se agrega un instante antes
    // de quitar "hidden" y se retira en el frame siguiente, nunca se usa
    // para la visibilidad inicial de la raíz.
    function showView(viewEl, fromLeft) {
      viewEl.classList.toggle("pm-mobile-view-from-left", !!fromLeft);
      viewEl.classList.add("pm-mobile-view-entering");
      viewEl.hidden = false;
      viewEl.setAttribute("aria-hidden", "false");
      viewEl.removeAttribute("inert");
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          viewEl.classList.remove("pm-mobile-view-entering");
        });
      });
    }

    function hideView(viewEl) {
      viewEl.hidden = true;
      viewEl.setAttribute("aria-hidden", "true");
      viewEl.setAttribute("inert", "");
      viewEl.classList.remove("pm-mobile-view-entering", "pm-mobile-view-from-left");
    }

    function openView(id, triggerEl) {
      if (!id) return;
      var currentId = stack[stack.length - 1];
      if (currentId === id) return;
      var targetEl = getView(id);
      if (!targetEl) return;
      var currentEl = getView(currentId);
      if (currentEl) hideView(currentEl);
      showView(targetEl, false);
      resetScroll(targetEl);
      stack.push(id);
      focusView(targetEl);
    }

    function goBack() {
      if (stack.length <= 1) return false;
      var closingId = stack.pop();
      var closingEl = getView(closingId);
      if (closingEl) hideView(closingEl);
      var targetId = stack[stack.length - 1];
      var targetEl = getView(targetId);
      if (targetEl) {
        showView(targetEl, true);
        resetScroll(targetEl);
        focusView(targetEl);
      }
      return true;
    }

    function resetToRoot() {
      while (stack.length > 1) {
        var closingId = stack.pop();
        var closingEl = getView(closingId);
        if (closingEl) hideView(closingEl);
      }
      var rootId = stack[0];
      var rootEl = getView(rootId);
      if (rootEl) {
        rootEl.classList.remove("pm-mobile-view-entering", "pm-mobile-view-from-left");
        rootEl.hidden = false;
        rootEl.setAttribute("aria-hidden", "false");
        rootEl.removeAttribute("inert");
        resetScroll(rootEl);
      }
    }

    drawerEl.addEventListener("click", function (event) {
      var openTrigger = event.target.closest("[data-pm-open]");
      if (openTrigger && drawerEl.contains(openTrigger)) {
        event.preventDefault();
        event.stopPropagation();
        openView(openTrigger.getAttribute("data-target"), openTrigger);
        return;
      }

      var backTrigger = event.target.closest("[data-pm-back]");
      if (backTrigger && drawerEl.contains(backTrigger)) {
        event.preventDefault();
        event.stopPropagation();
        goBack();
      }
      // Cualquier otro clic (enlaces finales, "Ver todo", tarjetas) no se
      // intercepta: navegan de forma nativa, y el listener delegado de
      // assets/scripts.js sigue viendo el evento normalmente.
    });

    escapeTarget.addEventListener("keydown", function (event) {
      if (event.key !== "Escape" && event.key !== "Esc") return;
      if (!document.body.classList.contains("mobile-menu-open")) return;
      if (goBack()) {
        event.stopPropagation();
        return;
      }
      if (toggle) {
        event.stopPropagation();
        toggle.click();
      }
    });

    drawerEl._pmReset = resetToRoot;

    // La raíz ya nace completamente visible desde el HTML (sin "hidden",
    // opacity:1 por defecto en CSS) -- este script no necesita hacer nada
    // para mostrarla la primera vez.
  }

  function init() {
    var drawers = document.querySelectorAll("[data-pm-mobile-drawer]");
    for (var i = 0; i < drawers.length; i++) {
      initDrawer(drawers[i]);
    }
  }

  function resetAllDrawers() {
    var drawers = document.querySelectorAll("[data-pm-mobile-drawer]");
    for (var i = 0; i < drawers.length; i++) {
      if (typeof drawers[i]._pmReset === "function") {
        drawers[i]._pmReset();
      }
    }
  }

  function bindGlobalListenersOnce() {
    // Disparado por assets/scripts.js (handleMobileMenuDismiss) cuando el
    // drawer nativo se cierra por cualquier vía -- un único listener a
    // nivel de documento que siempre vuelve a consultar los drawers en
    // vivo, así nunca se acumulan listeners nuevos en cada
    // shopify:section:load.
    document.addEventListener("mobile-menu:close", resetAllDrawers);

    // Si el viewport cruza a desktop con el drawer abierto, se restablece
    // el estado de vistas y se normaliza el cierre SIN reutilizar
    // toggle.click(). AUDITADO (root cause confirmada): toggle.click()
    // burbujeaba hacia el handler delegado de Athens (assets/scripts.js,
    // protegido) y terminaba en handleMobileMenuDismiss() ->
    // removeTrapFocus(mobileMenuToggle) -> mobileMenuToggle.focus() --
    // pero .head-slot-nav-mobile-link ya es display:none en desktop
    // (assets/base.css, protegido). Ese intento de foco sobre un botón
    // no enfocable es la causa confirmada de que "Pasar al contenido"
    // (.skip-link, layout/theme.liquid + assets/base.css, ambos
    // protegidos, .skip-link:focus sin :focus-visible) reaparezca: al
    // quedar sin destino de foco válido, el navegador termina asentando
    // el foco en el primer elemento enfocable real del documento, que es
    // el propio skip link.
    //
    // Se replica aquí el mismo estado final que
    // handleMobileMenuDismiss() deja en el DOM -- body sin
    // "mobile-menu-open", aria-expanded="false" en el toggle,
    // aria-hidden="true" y max-height reseteado en .mobile-menu, evento
    // "mobile-menu:close" -- pero SIN el .focus() problemático:
    // window.removeTrapFocus (assets/scripts.js, protegido, declaración
    // de función clásica expuesta en window porque scripts.js se carga
    // como <script> clásico sin type="module", confirmado en
    // snippets/head-scripts.liquid) se invoca SIN argumento, así que
    // solo libera los listeners del focus trap
    // (focusin/focusout/keydown) y nunca llama a ningún .focus() (ver su
    // propio código: "if (elementToFocus) elementToFocus.focus();") --
    // el foco actual del navegador queda intacto, nunca se mueve al
    // skip link. matchMedia + su evento "change" evita un listener de
    // resize sin debounce.
    var desktopBreakpoint = window.matchMedia("(max-width: 989px)");
    var handleBreakpointChange = function (event) {
      if (event.matches) return;
      resetAllDrawers();
      if (!document.body.classList.contains("mobile-menu-open")) return;

      var toggle = document.querySelector(".head-slot-nav-mobile-link");
      var menu = document.querySelector(".mobile-menu");

      document.body.classList.remove("mobile-menu-open");
      if (toggle) toggle.ariaExpanded = "false";
      if (menu) {
        menu.ariaHidden = "true";
        menu.style.maxHeight = "";
      }
      if (typeof window.removeTrapFocus === "function") {
        window.removeTrapFocus();
      }

      document.dispatchEvent(new CustomEvent("mobile-menu:close"));
    };
    if (typeof desktopBreakpoint.addEventListener === "function") {
      desktopBreakpoint.addEventListener("change", handleBreakpointChange);
    } else if (typeof desktopBreakpoint.addListener === "function") {
      desktopBreakpoint.addListener(handleBreakpointChange);
    }

    // El editor de Shopify reemplaza el HTML de la sección Header en cada
    // edición y dispara este evento (incluida la primera vista previa) --
    // init() es seguro de repetir: initDrawer() ignora cualquier elemento
    // ya inicializado vía WeakSet.
    document.addEventListener("shopify:section:load", init);
  }

  bindGlobalListenersOnce();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
